import { useEffect, useState, RefObject } from "react";
import throttle from "lodash-es/throttle";

/**
 * 获取父级容器宽度的自定义 Hook
 *
 * @param parentRef - 父级容器的 ref
 * @returns 计算后的宽度值（像素）
 */
const useParentWidth = (
  parentRef: RefObject<HTMLElement | HTMLDivElement | null> | null,
  styleProps: { padding: number; containerPadding: number },
): number => {
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    if (!parentRef?.current) {
      return;
    }

    const updateWidth = () => {
      if (parentRef.current) {
        const parentWidth = parentRef.current.offsetWidth;
        const calculatedWidth =
          parentWidth - styleProps.padding * 2 + styleProps.containerPadding;
        setWidth(calculatedWidth);
      }
    };
    const throttledUpdateWidth = throttle(updateWidth, 80, {
      leading: true,
      trailing: true,
    });

    // 初始计算
    updateWidth();

    // 创建 ResizeObserver 来监听父级容器尺寸变化
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === parentRef.current) {
          throttledUpdateWidth();
        }
      }
    });

    // 开始观察
    resizeObserver.observe(parentRef.current);

    // 监听窗口大小变化（作为备用方案）
    const handleResize = () => {
      throttledUpdateWidth();
    };

    window.addEventListener("resize", handleResize);

    // 清理函数
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
      throttledUpdateWidth.cancel();
    };
  }, [parentRef]);

  return width;
};

/**
 * 获取父级容器宽度并返回 CSS 样式对象的 Hook
 *
 * @param parentRef - 父级容器的 ref
 * @returns 包含 width 的样式对象
 */
export const useParentWidthStyle = (
  parentRef: RefObject<HTMLElement | HTMLDivElement | null> | null,
  styleProps: { padding: number; containerPadding: number },
): { width: string } => {
  const width = useParentWidth(parentRef, styleProps);

  return {
    width: width > 0 ? `${width}px` : "0px",
  };
};
